import { useState, useEffect, useRef } from 'react';
import history from '../../../root/history';
import styled from 'styled-components';
import { Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Tooltip2 } from '@blueprintjs/popover2';
import moment from 'moment';
import ContextMenu from '../../context-menu';
// import TimedPopover from '/src/components/sidebar/timedPopover';

const Item = styled.h3`
  color: ${({ selected, depth }) =>
    selected && depth === 0
      ? 'var(--sidebar-active-text)'
      : selected && depth > 0
        ? 'var(--sidebar-active-text)'
        : 'var(--sidebar-text)'};
  font-size: ${({ selected, depth }) =>
    selected && depth > 0 ? '0.95rem' : '0.9rem'};
  background: ${({ selected, depth }) =>
    selected && depth === 0 ? 'var(--menu-active)' : null};
  font-weight: ${({ selected }) => (selected ? 'bold' : 'normal')};
  padding: 0.5rem 1.25rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0;
  padding-left: ${({ depth }) => (depth > 0 ? `calc(calc(16px * ${depth}) + 2.25rem)` : null)};
  margin: 0;

  &:hover {
    color: ${({ selected, depth }) =>
      selected && depth === 0
        ? 'var(--sidebar-active-text)'
        : 'var(--sidebar-hover-text)'};
  }
`;
// Shared scroll container for a run of titled child rows (chat threads or
// reports): auto-height up to ~10 rows (0.9rem text + 1rem vertical padding
// each), then scrolls; collapses to zero height when the run is empty.
const ScrollGroup = styled.div`
  max-height: 21rem;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--sidebar-divider);
    border-radius: 3px;
  }
`;
const RowActions = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex: 0 0 auto;
  color: var(--sidebar-text);
  visibility: ${({ forceVisible }) => (forceVisible ? 'visible' : 'hidden')};

  ${Item}:hover & {
    visibility: visible;
  }

  .bp3-icon {
    opacity: 0.85;
    cursor: pointer;
  }
  .bp3-icon:hover {
    opacity: 1;
  }
`;
const RowActionButton = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: ${({ inactive }) => (inactive ? 0.4 : 1)};
`;
const RenameInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  margin-right: 0.5rem;
  background: transparent;
  border: 1px solid var(--sidebar-divider);
  border-radius: 3px;
  color: var(--sidebar-text);
  font-family: inherit;
  font-size: 0.9rem;
  padding: 0.1rem 0.3rem;
`;
const ValidationLine = styled.div`
  color: #c23030;
  font-size: 0.75rem;
  padding: 0.1rem 1.25rem 0.25rem 2.25rem;
`;
const ItemIcon = styled(Icon)`
  margin-right: 1rem;
`;
const Label = styled.span`
  /* Single line, ellipsis on overflow — never wrap (restores pre-pin behaviour
     when the pin icon + scrollbar narrow the row). */
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const RightAction = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  color: var(--sidebar-text);

  .bp3-icon {
    opacity: 0.85;
  }

  &:hover .bp3-icon {
    opacity: 1;
  }
`;

const Caret = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  color: var(--sidebar-text);
  .bp3-icon { opacity: 0.85; }
  &:hover .bp3-icon { opacity: 1; }
`;

const TooltipWrapper = styled.div`
  display: block;
  width: 100%;
`;

const MenuItem = ({ item, depth = 0, location }) => {
  const getSelected = (route, depth, exactMatch = false) => {
    let selected = false;
    if (!route) return false;
    if (route.includes('?')) route = route.split('?')[0];
    if (route === window.location.pathname) selected = true;

    // Skip partial matching if exactMatch is requested or if already selected
    if (!selected && !exactMatch) {
      if (depth === 0 && typeof window === 'object') {
        // find the first part of the route that is not blank
        const routeFirstPart = route.split('/').find((str) => !!str);
        // find the first part of the window.location.pathname that is not blank
        const currentPathFirstPart = window.location.pathname
          .split('/')
          .find((str) => !!str);
        selected = routeFirstPart === currentPathFirstPart;
      }
    }

    return selected;
  };

  // For accordions, check if any child route is selected
  const getChildSelected = (children) => {
    if (!children || children.length === 0) return false;
    return children.some((child) => child.path && getSelected(child.path, 1));
  };

  const isAccordion = depth === 0 && item.isAccordion === true;
  const [selected, setSelected] = useState(
    isAccordion ? getChildSelected(item.children) : getSelected(item.path, depth, item.exactMatch)
  );
  const [expanded, setExpanded] = useState(isAccordion ? getChildSelected(item.children) : false);

  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  // Seed the draft from the raw title when entering edit mode. Deliberately omit
  // renameInitialValue from the deps — re-including it would clobber the user's
  // draft on any background re-render.
  useEffect(() => {
    if (!item.isEditing) return undefined;
    setDraft(item.renameInitialValue || '');
    // Focus + select-all once the seeded value has rendered.
    const t = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.isEditing]);

  const renameError = item.isEditing && item.validateRename
    ? item.validateRename(draft)
    : null;
  const saveDisabled = !!renameError || isSaving;

  const handleRenameSave = async () => {
    if (saveDisabled) return;
    const trimmed = draft.trim();
    if (trimmed === (item.renameInitialValue || '').trim()) {
      item.onRenameCancel();
      return;
    }
    setIsSaving(true);
    try {
      await item.onRenameSave(trimmed);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRenameSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      item.onRenameCancel();
    }
  };

  const getOnClick = (route, location) => (e) => {
    if (item.isEditing) return;
    if (isAccordion) {
      setExpanded((prev) => !prev);
      return;
    }

    history.push(route, location.state);
  };

  useEffect(() => {
    return history.listen((location) => {
      const sel = isAccordion
        ? getChildSelected(item.children)
        : getSelected(item.path, depth, item.exactMatch);
      setSelected(sel);
      if (isAccordion) setExpanded(sel);
    });
  }, [depth, item.path, item.children, item.exactMatch, isAccordion]);

  return (
    <>
      <Item
        key={item.label}
        onClick={getOnClick(item.path, location)}
        selected={selected}
        depth={depth || 0}
      >
        <ItemIcon icon={item.icon} />
        {item.isEditing ? (
          <RenameInput
            ref={inputRef}
            value={draft}
            maxLength={item.renameMaxLength}
            disabled={isSaving}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleRenameKeyDown}
          />
        ) : (
          <Label>{item.label}</Label>
        )}
        {(item.onTogglePin || item.contextMenuItems) && (
          <RowActions forceVisible={!!item.menuOpen || !!item.isEditing}>
            {item.isEditing ? (
              <>
                <RowActionButton
                  role="button"
                  aria-label="Save"
                  inactive={saveDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRenameSave();
                  }}
                >
                  <Icon icon="tick" size={16} />
                </RowActionButton>
                <RowActionButton
                  role="button"
                  aria-label="Cancel"
                  inactive={isSaving}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isSaving) item.onRenameCancel();
                  }}
                >
                  <Icon icon="cross" size={16} />
                </RowActionButton>
              </>
            ) : (
              <>
                {item.onTogglePin && (
                  <RowActionButton
                    role="button"
                    aria-label={item.pinned ? 'Unpin chat' : 'Pin chat'}
                    onClick={(e) => {
                      e.stopPropagation(); // Item's own onClick navigates — must not fire
                      if (item.onTogglePin) item.onTogglePin();
                    }}
                  >
                    <Icon
                      icon={item.pinned ? IconNames.UNPIN : IconNames.PIN}
                      size={16}
                    />
                  </RowActionButton>
                )}
                {item.contextMenuItems && (
                  <ContextMenu
                    items={item.contextMenuItems}
                    isOpen={!!item.menuOpen}
                    onClose={() =>
                      item.onMenuOpenChange && item.onMenuOpenChange(false)
                    }
                  >
                    <RowActionButton
                      role="button"
                      aria-label="More options"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.onMenuOpenChange)
                          item.onMenuOpenChange(!item.menuOpen);
                      }}
                    >
                      <Icon icon={IconNames.MORE} size={16} />
                    </RowActionButton>
                  </ContextMenu>
                )}
              </>
            )}
          </RowActions>
        )}
        {isAccordion && (
          <Caret
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
            aria-label={expanded ? 'collapse' : 'expand'}
            role="button"
          >
            <Icon icon={expanded ? 'caret-up' : 'caret-down'} />
          </Caret>
        )}
        {depth === 0 && item.action && item.action.path && (
          <RightAction
            onClick={(e) => {
              e.stopPropagation();
              history.push(item.action.path);
            }}
            aria-label={item.action.tooltip || 'action'}
            role="button"
          >
            <Tooltip2 content={item.action.tooltip || ''}>
              <Icon icon={item.action.icon || 'plus'} />
            </Tooltip2>
          </RightAction>
        )}
      </Item>
      {item.isEditing && renameError && (
        <ValidationLine>{renameError}</ValidationLine>
      )}
      {(isAccordion ? expanded : (selected || item.alwaysExpanded)) &&
        item.children &&
        (() => {
          // Wrap a child in a full-title hover tooltip. Threads also show the
          // date prefix ("06 Jan 2026: <title>"); reports show the title alone.
          const renderTitledChild = (child, idx) => {
            const tooltipContent = child.updatedAt
              ? `${moment(child.updatedAt).format('DD MMM YYYY')}: ${child.fullTitle}`
              : child.fullTitle;

            return (
              <TooltipWrapper key={child.path || child.id || `child-${idx}`}>
                <Tooltip2
                  content={tooltipContent}
                  position="bottom"
                  hoverOpenDelay={300}
                  openOnTargetFocus={false}
                  disabled={!!child.menuOpen || !!child.isEditing}
                  fill
                >
                  <div>
                    <MenuItem
                      item={child}
                      depth={depth + 1}
                      location={location}
                    />
                  </div>
                </Tooltip2>
              </TooltipWrapper>
            );
          };

          // Runs of titled children (chat threads or reports) render inside a
          // shared scroll container; any non-titled children (e.g. settings)
          // sit outside the containers and flush the current run.
          const elements = [];
          let group = [];
          let groupStart = 0;
          const flushGroup = () => {
            if (group.length === 0) return;
            elements.push(
              <ScrollGroup key={`scroll-group-${groupStart}`}>
                {group}
              </ScrollGroup>,
            );
            group = [];
          };

          item.children.forEach((child, idx) => {
            // Any titled child (chat thread or report) accumulates into the run
            if (child.fullTitle) {
              if (group.length === 0) groupStart = idx;
              group.push(renderTitledChild(child, idx));
              return;
            }

            flushGroup();
            // Use child.path which includes the report id to avoid duplicate keys when names repeat
            elements.push(
              <MenuItem
                key={child.path || child.id || child.label || `child-${idx}`}
                item={child}
                depth={depth + 1}
                location={location}
              />,
            );
          });
          flushGroup();
          return elements;
        })()}
    </>
  );
};

export default MenuItem;
